import {
  LinearProgress,
  styled,
  Typography,
  linearProgressClasses,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useMemo, useState, useEffect } from 'react'
import { FileUploader } from 'react-drag-drop-files'
import ButtonBase from 'design-system/button'
import { LoopOutlined } from '@mui/icons-material'
import { useAppSelector } from 'reduxStore/store'
import { uploadCxrayExam, uploadEcgExam } from 'services/exam'
import { parseEcgDatFile, IEcgLeads } from 'utils/ecg'
import NavLinkBack from 'components/NavLinkBack'
import { CXRAY_ANALYSIS, ECG_ANALYSIS } from 'navigation/CONSTANTS'
import { getAvailableExamTypes } from 'utils/permissions'
import { CXRAY_SUBTYPE_OPTIONS } from 'pages/Diagnostics/constants'
import { ExamSubtype } from 'types/exam/types'
import analytics, { fileSizeBucket } from 'services/analytics'

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 15,
  borderRadius: 16,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800]
    })
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: 'var(--primarySoft-button-color)',
    ...theme.applyStyles('dark', {
      backgroundColor: 'var(--primarySoft-button-color)'
    })
  }
}))

type ExamType = 'xray' | 'ecg'

const EXAM_FILE_TYPES: Record<ExamType, string[]> = {
  xray: ['PNG'],
  ecg: ['DAT']
}

const EXAM_LABELS: Record<ExamType, string> = {
  xray: 'Raio-X',
  ecg: 'ECG'
}

interface UploadDiagnosticsPageProps {
  /** Tipo de exame pré-selecionado ao abrir o upload a partir de uma listagem */
  initialExamType?: ExamType
}

export const UploadDiagnosticsPage = ({
  initialExamType
}: UploadDiagnosticsPageProps = {}) => {
  const establishmentData = useAppSelector(rState => rState.establishment.data)

  // Filter exam types based on permissions
  const availableExamTypes = useMemo(() => {
    return getAvailableExamTypes(establishmentData?.modules_permission)
  }, [establishmentData?.modules_permission])

  const [state, setState] = useState<IStateStdTypes>('idle')
  const [examType, setExamType] = useState<ExamType>(
    initialExamType || availableExamTypes[0] || 'xray'
  )

  // Caminho de retorno para a listagem de origem (Raio-X ou ECG)
  const backTo =
    initialExamType === 'ecg' ? `/${ECG_ANALYSIS}` : `/${CXRAY_ANALYSIS}`
  const [file, setFile] = useState<File | null>(null)
  const [fileBase64, setFileBase64] = useState<string>()
  const [patientId, setPatientId] = useState<string>('')
  const [subtype, setSubtype] = useState<ExamSubtype | ''>('')
  const [ecgData, setEcgData] = useState<IEcgLeads | null>(null)

  const establishmentId = useMemo(() => {
    return establishmentData?.establishment_id
  }, [establishmentData])

  useEffect(() => {
    if (initialExamType === 'xray') {
      analytics.track('xray_page_viewed', { page: 'upload' })
    }
  }, [initialExamType])

  // Update examType when permissions change.
  // Quando o tipo vem fixo da tela de origem (initialExamType), não alteramos.
  useEffect(() => {
    if (
      !initialExamType &&
      availableExamTypes.length > 0 &&
      !availableExamTypes.includes(examType)
    ) {
      setExamType(availableExamTypes[0])
    }
  }, [availableExamTypes, examType, initialExamType])

  const handleExamTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExamType(event.target.value as ExamType)
    setFile(null)
    setFileBase64(undefined)
    setPatientId('')
    setSubtype('')
    setEcgData(null)
    setState('idle')
  }

  const handleFileChange = async (newFile: File) => {
    setFile(newFile)

    if (examType === 'xray') {
      // Para Raio-X, apenas converte para base64
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result as string
        setFileBase64(base64)
      }
      reader.readAsDataURL(newFile)
    } else if (examType === 'ecg') {
      // Para ECG, parseia o arquivo DAT imediatamente
      try {
        setState('loading')
        const parsedData = await parseEcgDatFile(newFile)
        setEcgData(parsedData)
        setState('idle')
        console.log('Arquivo ECG parseado com sucesso ao selecionar')
      } catch (error) {
        setState('error')
        console.error('Erro ao parsear ECG:', error)
        alert(
          `Erro ao processar arquivo ECG: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
        )
      }
    }
  }

  const handleSubmit = () => {
    if (examType === 'xray' && fileBase64) {
      processFile(fileBase64)
    } else if (examType === 'ecg') {
      processFile('')
    }
  }

  const processFile = async (fileInBase64: string) => {
    // Validações
    if (!patientId.trim()) {
      alert('Por favor, informe o ID do paciente')
      return
    }

    if (examType === 'xray' && !subtype) {
      alert('Por favor, selecione o subtipo do exame de Raio-X')
      return
    }

    if (!establishmentId) {
      alert('É necessário estar em uma organização para fazer upload de exames')
      return
    }

    const trackXrayUpload = (
      name:
        | 'xray_upload_submitted'
        | 'xray_upload_succeeded'
        | 'xray_upload_failed'
    ) => {
      if (examType !== 'xray' || !subtype) return
      analytics.track(name, {
        size_bucket: fileSizeBucket(file?.size ?? 0),
        subtype
      })
    }

    setState('loading')
    try {
      if (examType === 'xray') {
        trackXrayUpload('xray_upload_submitted')
        // Processar Raio-X
        const base64Image = fileInBase64.split(',')[1] // Remove o prefixo data:image/png;base64,

        const response = await uploadCxrayExam({
          patient_id: patientId,
          hospital_id: establishmentId,
          image: base64Image,
          ...(subtype ? { subtype } : {})
        })

        if (response.success) {
          trackXrayUpload('xray_upload_succeeded')
          setState('success')
        } else {
          trackXrayUpload('xray_upload_failed')
          setState('error')
        }
      } else if (examType === 'ecg') {
        // Processar ECG - usa dados já parseados
        if (!ecgData) {
          alert('Dados do ECG não foram carregados corretamente')
          setState('error')
          return
        }

        const response = await uploadEcgExam({
          patient_id: patientId,
          hospital_id: establishmentId,
          ...ecgData
        })

        if (response.success) {
          setState('success')
        } else {
          setState('error')
        }
      }
    } catch (ex) {
      trackXrayUpload('xray_upload_failed')
      setState('error')
      console.error(ex)
      alert(
        `Erro ao processar arquivo: ${ex instanceof Error ? ex.message : 'Erro desconhecido'}`
      )
    }
  }

  const currentFileTypes = EXAM_FILE_TYPES[examType]

  const render = {
    idle: (
      <>
        {availableExamTypes.length === 0 ? (
          <Grid size={12}>
            <Alert severity='warning'>
              Este estabelecimento não possui permissões para realizar uploads
              de exames diagnósticos. Entre em contato com o administrador para
              solicitar as permissões necessárias.
            </Alert>
          </Grid>
        ) : (
          <>
            <Grid size={12}>
              <FormControl>
                <FormLabel id='exam-type-label'>
                  <Typography variant='h6'>Tipo de Exame</Typography>
                </FormLabel>
                {initialExamType ? (
                  // Tipo fixo pela tela de origem (Raio-X ou ECG)
                  <Typography variant='body1' sx={{ mt: 1 }}>
                    {EXAM_LABELS[examType]} (
                    {EXAM_FILE_TYPES[examType].join(', ')})
                  </Typography>
                ) : (
                  <RadioGroup
                    row
                    aria-labelledby='exam-type-label'
                    name='exam-type'
                    value={examType}
                    onChange={handleExamTypeChange}
                  >
                    {availableExamTypes.includes('xray') && (
                      <FormControlLabel
                        value='xray'
                        control={<Radio />}
                        label='Raio-X (PNG)'
                      />
                    )}
                    {availableExamTypes.includes('ecg') && (
                      <FormControlLabel
                        value='ecg'
                        control={<Radio />}
                        label='ECG (DAT)'
                      />
                    )}
                  </RadioGroup>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label='ID do Paciente'
                value={patientId}
                onChange={e => setPatientId(e.target.value)}
                placeholder='Digite o ID do paciente'
                required
              />
            </Grid>
            {examType === 'xray' && (
              <Grid size={12}>
                <FormControl fullWidth required>
                  <InputLabel id='cxray-subtype-label'>
                    Subtipo do Raio-X
                  </InputLabel>
                  <Select
                    labelId='cxray-subtype-label'
                    label='Subtipo do Raio-X'
                    value={subtype}
                    onChange={(e: SelectChangeEvent) =>
                      setSubtype(e.target.value as ExamSubtype)
                    }
                  >
                    {CXRAY_SUBTYPE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid
              size={12}
              spacing={3}
              display='flex'
              justifyContent='center'
              sx={{
                '& label': {
                  width: '100%',
                  maxWidth: '100%',
                  minHeight: '200px',
                  paddingInline: '32px'
                }
              }}
            >
              <FileUploader
                key={`${examType}-${file?.name ?? ''}`}
                handleChange={handleFileChange}
                name='file'
                types={currentFileTypes}
                label={
                  file
                    ? `✓ ${file.name}`
                    : `Selecione ou arraste aqui um arquivo ${EXAM_LABELS[examType]} (${currentFileTypes.join(', ')}) para fazer a avaliação`
                }
                uploadedLabel={file?.name}
              />
            </Grid>

            {file && (
              <Grid size={12} textAlign='right' mt={2}>
                <ButtonBase
                  variant='contained'
                  onClick={handleSubmit}
                  disabled={
                    !patientId.trim() ||
                    !establishmentId ||
                    (examType === 'xray' && !subtype)
                  }
                >
                  Processar Exame
                </ButtonBase>
              </Grid>
            )}
          </>
        )}
      </>
    ),
    success: (
      <>
        <Grid size={12}>
          <Alert severity='success'>
            Arquivo processado com sucesso! Tipo: {EXAM_LABELS[examType]}
          </Alert>
        </Grid>
        <Grid size={12}>
          <Typography variant='body1'>Arquivo: {file?.name}</Typography>
        </Grid>
        <Grid size={12}>
          <ButtonBase
            onClick={() => {
              setState('idle')
              setFile(null)
              setFileBase64(undefined)
              setPatientId('')
              setSubtype('')
              setEcgData(null)
            }}
          >
            <LoopOutlined /> Fazer novo upload
          </ButtonBase>
        </Grid>
      </>
    ),
    error: (
      <>
        <Grid size={6} margin='0 auto'>
          <Alert severity='error'>
            Houve um erro ao tentar processar o arquivo, tente novamente!
          </Alert>
        </Grid>
        <Grid size={12}>
          <ButtonBase onClick={() => setState('idle')}>
            <LoopOutlined /> Tentar novamente
          </ButtonBase>
        </Grid>
      </>
    ),
    loading: (
      <>
        <Grid size={12}>
          <Typography variant='body1'>
            Processando arquivo {EXAM_LABELS[examType]}...
          </Typography>
        </Grid>
        <Grid size={8} margin='0 auto'>
          <BorderLinearProgress variant='indeterminate' />
        </Grid>
      </>
    )
  }

  return (
    <Grid
      container
      spacing={2}
      maxWidth={1200}
      margin={'0 auto'}
      mb={3}
      sx={{
        overflow: { xs: 'hidden', md: 'visible' },
        wordBreak: { xs: 'break-all', md: 'normal' }
      }}
    >
      <Grid size={12} paddingBlock={2}>
        <NavLinkBack to={backTo} title='Lista de Análises' />
      </Grid>
      <Grid size={12}>
        <Typography variant='h5'>Upload de Exames Diagnósticos</Typography>
      </Grid>
      <Grid size={12}>
        <Typography variant='body1'>
          Faça upload de exames de Raio-X ou ECG para análise
        </Typography>
      </Grid>
      <Grid container size={12} mt={4}>
        {render[state]}
      </Grid>
    </Grid>
  )
}
